/**
 * Authorisation over the workspace roles — design §8.8, product decision Q10, unit P5-03.
 *
 * AMENDED 2026-10-05 (plan-014 A3, design §27.5.1): this module is now a COMPATIBILITY FACE over
 * the canonical evaluator in `./capability.ts`, kept for one release so existing imports keep
 * compiling. Nothing here restates a tick: every answer below is the workspace matrix of
 * `capability.ts`, which `capability.test.ts` holds equal to A1's fixture
 * (`./fixture/capability-matrix.json`) cell by cell. New code imports from `capability.ts`.
 *
 * WHAT CHANGED UNDER THE OLD NAMES.
 *   * `Capability` / `CAPABILITY` are the WORKSPACE capabilities of design §27.5.3. The retired
 *     names `billing.write`, `workspace.create`, `workspace.delete`, `sso.read` and `sso.write` left
 *     the union (they are account capabilities now — `RETIRED_WORKSPACE_CAPABILITY` in
 *     `capability.ts` says where each went) and every call site was rewritten.
 *   * `ROLE_CAPABILITY` has a row for each of the seven member roles (the four of Q10 and the three
 *     presets of migration 0079).
 *
 * THE CRITICAL SENTENCE STILL HOLDS. "Members never see recordings or caller details" is a DATABASE
 * PREDICATE, not a UI check: `recording_visible`, `conversation_visible` and the restrictive write
 * policies are the first line (re-expressed through `workspace_capability_roles()` in 0079), and
 * `packages/db/src/identity/predicate.test.ts` asserts the database agrees with the matrix.
 */
import { MEMBER_ROLE, RECORDING_VISIBLE_ROLE } from "../account/account.js";
import { WORKSPACE_CAPABILITY, holdsWorkspaceCapability, workspaceCapabilityOf, workspaceRolesHolding, } from "./capability.js";
/** Every workspace capability the console's routes gate on (design §27.5.3's order). */
export const CAPABILITY = WORKSPACE_CAPABILITY;
/** The workspace matrix, one row per member role, derived from `capability.ts`. */
export const ROLE_CAPABILITY = Object.freeze(Object.fromEntries(MEMBER_ROLE.map((role) => [role, workspaceCapabilityOf(role)])));
/** Whether a role holds a workspace capability. */
export function roleHolds(role, capability) {
    return holdsWorkspaceCapability(role, capability);
}
/** Every role that holds a workspace capability, in `MEMBER_ROLE` order. */
export function rolesHolding(capability) {
    return workspaceRolesHolding(capability);
}
/** The roles that may play a recording, derived from the matrix rather than restated. */
export const RECORDING_CAPABLE_ROLE = rolesHolding("recording.play");
/** Kept honest by a suite: the matrix and AC-33's constant must never disagree. */
export const RECORDING_ROLE_AGREES = RECORDING_CAPABLE_ROLE.length === RECORDING_VISIBLE_ROLE.length &&
    RECORDING_CAPABLE_ROLE.every((role) => RECORDING_VISIBLE_ROLE.includes(role));
/**
 * The impersonation deny list and the read/mutate rule, re-exported under their old import path.
 * They now span both matrices (design §27.5.5); `capability.ts` is where they live.
 */
export { IMPERSONATION_DENIED_CAPABILITY, capabilityMutates, deniedUnderImpersonation } from "./capability.js";
//# sourceMappingURL=authorisation.js.map